import { useMemo } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from "recharts";
import { PROVINCE_NAMES, toneFor } from "../lib/tone";

const fmtDay = (d) => new Date(d + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric" });
const lastValue = (arr) => [...arr].reverse().find((v) => v !== null);

export const TrendChart = ({ trend, code }) => {
  const rows = useMemo(() => trend?.dates.map((d, i) => ({ day: fmtDay(d), CA: trend.series.CA[i], R: code ? trend.series[code][i] : null })) || [], [trend, code]);
  if (!trend) return <div className="h-72 animate-pulse rounded-xl bg-slate-50" />;
  return (
    <div data-testid="trend-chart" className="h-72 w-full">
      <ResponsiveContainer>
        <LineChart data={rows} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" minTickGap={24} />
          <YAxis domain={[0, 100]} ticks={[0, 40, 50, 60, 100]} tick={{ fontSize: 11, fill: "#64748b" }} />
          <ReferenceLine y={50} stroke="#94a3b8" strokeDasharray="4 4" />
          <Tooltip formatter={(v, n) => [v ?? "–", n === "CA" ? "Canada" : PROVINCE_NAMES[code]]} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
          <Line type="monotone" dataKey="CA" stroke="#0C4A6E" strokeWidth={2} dot={false} name="CA" />
          {code && <Line type="monotone" dataKey="R" stroke="#10b981" strokeWidth={2.5} dot={false} name="R" />}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const Sparkline = ({ values }) => {
  const pts = values.map((v, i) => (v === null ? null : [(i / (values.length - 1)) * 100, 30 - (v / 100) * 30])).filter(Boolean);
  if (pts.length < 2) return <div className="flex h-8 items-center text-[11px] text-slate-400">Building history…</div>;
  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="h-8 w-full">
      <line x1="0" x2="100" y1="15" y2="15" stroke="#e2e8f0" strokeDasharray="2 2" />
      <polyline points={pts.map((p) => p.join(",")).join(" ")} fill="none" stroke={toneFor(lastValue(values)).hex} strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export const TrendGrid = ({ trend, selected, onSelect }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="trend-grid">
    {Object.keys(PROVINCE_NAMES).map((c) => {
      const vals = trend?.series[c] || [];
      const last = lastValue(vals);
      return (
        <button key={c} onClick={() => onSelect(selected === c ? null : c)} data-testid={`trend-spark-${c}`} aria-pressed={selected === c}
          className={`rounded-2xl border p-3 text-left transition-colors ${selected === c ? "border-sky-900 bg-sky-50" : "border-slate-200 bg-white hover:border-sky-300"}`}>
          <div className="flex items-center justify-between text-xs">
            <span className="font-heading font-semibold text-slate-800">{c}</span>
            <span className="font-mono text-slate-500">{last !== undefined ? Math.round(last) : "–"}</span>
          </div>
          <Sparkline values={vals} />
        </button>
      );
    })}
  </div>
);
